import clsx from "clsx";
import {
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import {
  ModalPanelBody,
  ModalPanelFooter,
  ModalPanelHeader,
} from "../ModalPanel";

type DrawerProps = {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  ariaLabel: string;
  className?: string;
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const DrawerRoot = ({
  open,
  onClose,
  children,
  ariaLabel,
  className,
}: DrawerProps) => {
  const [visible, setVisible] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerElRef = useRef<Element | null>(null);

  useBodyScrollLock(open);

  // Slide in on the frame after mount so the transition actually animates.
  useLayoutEffect(() => {
    if (!open) return;
    triggerElRef.current = document.activeElement;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setVisible(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [open]);

  const requestClose = () => {
    setVisible(false);
    window.setTimeout(onClose, 200);
  };

  useEffect(() => {
    if (!open) return;

    const focusTimer = window.setTimeout(() => {
      const first =
        panelRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      first?.focus();
    }, 100);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        requestClose();
        return;
      }
      if (e.key === "Tab" && panelRef.current) {
        const focusable = Array.from(
          panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      window.clearTimeout(focusTimer);
      (triggerElRef.current as HTMLElement | null)?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-40">
      <div
        className={clsx(
          "absolute inset-0 bg-surface-tonal-boldest transition-opacity duration-200",
          visible ? "opacity-100" : "opacity-0",
        )}
        onClick={requestClose}
        aria-hidden="true"
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        className={clsx(
          // No `overflow-hidden` here: this panel's `translate-x-*` is a
          // `transform`, which makes it the containing block for any `fixed`
          // descendant (e.g. a `!fixed` datepicker popup escaping
          // ModalPanelBody's `overflow-auto`) — if this element also clipped
          // with `overflow-hidden`, that popup would render contained by
          // (and cut off by) this panel instead of actually visible.
          // ModalPanelBody/Header/Footer already scope their own overflow;
          // nothing here relies on this panel doing it too.
          "absolute right-0 top-0 flex h-full w-full max-w-[680px] flex-col bg-surface-primary shadow-xl transition-transform duration-200",
          visible ? "translate-x-0" : "translate-x-full",
          className,
        )}
      >
        {children}
      </aside>
    </div>,
    document.body,
  );
};

export const Drawer = Object.assign(DrawerRoot, {
  Header: ModalPanelHeader,
  Body: ModalPanelBody,
  Footer: ModalPanelFooter,
});
